import { omitBy, isNull, isNil, isEmpty } from "lodash";
import { useTranslation } from "react-i18next";
import React, { useState, useEffect } from "react";
import errorHandler from "Utils/error-handler";
import { LineSchema } from "./line-form-validation";
import { toNum } from "Helpers/get-to-num";
import useQuery from "Hooks/query.hook";
import "../delivery-forms.scss";
import { Col, Row } from "antd";
import {
  AlertBox,
  Button,
  TextInput,
  SelectInput,
  RadioGroup,
} from "@life-line-lab/components-react";
import { CloseOutlined, PlusCircleOutlined } from "@ant-design/icons";

interface LineFormProps {
  onSubmit: (data: any) => void;
  onUpdate: (data: any) => void;
  onFetch: (lineID: string) => void;
  data?: any;
  trainLineOptions?: any;
}

const LineForm: React.FC<LineFormProps> = ({
  onSubmit,
  onUpdate,
  onFetch,
  data,
  trainLineOptions,
}) => {
  const query = useQuery();
  const id = query.get("id");
  const { t } = useTranslation();
  const action = query.get("action");

  const initialInput = {
    name: "",
    stations: [
      {
        stationName: "",
        stationCode: "",
        interStationETA_s: "0",
        interStationDistance_m: "0",
        coordinate: { lon: "", lat: "" },
        trainLineIDs: [],
        interchangeable: false,
      },
      {
        stationName: "",
        stationCode: "",
        interStationETA_s: "",
        interStationDistance_m: "",
        coordinate: { lon: "", lat: "" },
        trainLineIDs: [],
        interchangeable: false,
      },
    ],
  };
  const initialError = {};

  const [input, setInput] = useState<any>(initialInput);
  const [error, setError] = useState<any>(initialError);

  useEffect(() => {
    if (id) {
      onFetch(id);
    }
    document.body.classList.add("stack-screen--open");
    return () => document.body.classList.remove("stack-screen--open");
  }, []);

  useEffect(() => {
    if (!isEmpty(data)) {
      setInput({
        ...input,
        _id: data._id,
        name: data.name,
        stations: data?.stations?.map((station) => ({
          ...station,
          cumulativeETA_s: station?.cumulativeETA_s,
          interStationETA_s: station?.interStationETA_s,
          // backward compatibility
          interStationDistance_m: station?.interStationDistance_m ?? "0",
        })),
      });
    }
  }, [data]);

  const onRadioChange = (e, i) => {
    let oldValue = input.stations;
    let newValues = JSON.parse(JSON.stringify(oldValue));
    newValues[i]["interchangeable"] = e.target.value;
    switch (e.target.value) {
      case true:
        setInput({ ...input, stations: newValues });
        break;
      case false:
        newValues[i]["trainLineIDs"] = [];
        setInput({ ...input, stations: newValues });
        break;
      default:
        setInput({ ...input, stations: newValues });
    }
  };

  const addRows = () => {
    let stations = input.stations.concat({
      stationName: "",
      interStationETA_s: "",
      coordinate: { lon: "", lat: "" },
      trainLineIDs: [],
      interchangeable: false,
    });
    setInput({ ...input, ["stations"]: stations });
  };

  const deleteRows = (index) => {
    let stationList = input.stations;
    stationList.splice(index, 1);
    setError(initialError);
    setInput({ ...input, ["stations"]: stationList });
  };

  const handleFieldChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setError(initialError);
    setInput({ ...input, [e.target.name]: e.target.value });
  };

  const handleSelectChange = (value: string | string[], name: string, i) => {
    let trainLineList = JSON.parse(
      JSON.stringify(input.stations[i].trainLineIDs),
    );
    trainLineList = value;
    let newValues = JSON.parse(JSON.stringify(input.stations));
    newValues[i]["trainLineIDs"] = trainLineList;
    setInput({ ...input, stations: newValues });
    setError({ ...error, [name]: null });
  };

  const handleCoordinatesChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    i,
  ) => {
    const { name, value } = e.currentTarget;
    let newValues = JSON.parse(JSON.stringify(input.stations));
    newValues[i]["coordinate"][name] = value;
    setInput({ ...input, stations: newValues });
  };

  const handleRowInputChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    i: string,
  ) => {
    const { name, value } = e.currentTarget;
    let stationList = input.stations;
    let newValues = JSON.parse(JSON.stringify(stationList));
    newValues[i][name] = value;

    setInput({ ...input, stations: newValues });
    setError(initialError);
  };

  const handleSubmit = () => {
    try {
      const valInput = {
        ...input,
        stations: input.stations.map((station) => {
          return omitBy(
            {
              ...station,
              stationCode: isEmpty(station.stationCode)
                ? null
                : station.stationCode,
              stationName: station.stationName,
              interStationETA_s: toNum(station.interStationETA_s),
              interStationDistance_m: toNum(station.interStationDistance_m),
              coordinate: { ...station.coordinate },
              interchangeable: station.interchangeable,
              trainLineIDs: station.trainLineIDs,
            },
            isNil,
          );
        }),
      };

      const value = LineSchema.validateSync(omitBy(valInput, isNull), {
        strict: false,
        abortEarly: true,
        stripUnknown: true,
      });

      id ? onUpdate(value) : onSubmit(value);
    } catch (err) {
      console.log(err);
      const { path, value }: any = errorHandler.validation(err);

      console.log(path, value);
      setError({
        ...error,
        [path]: t(`${value}`, { field: t(`label.${path}`) }),
      });
    }
  };

  return (
    <div className="delivery-form">
      <div className="main-header">
        <span className="center header-title">
          {action != "view"
            ? t(id ? "label.editField" : "label.addField", {
                field: t("label.line"),
              }).toUpperCase()
            : t("label.line").toUpperCase()}
        </span>
      </div>
      <Row className="mt-32" justify="center">
        <Col xs={24} sm={24} md={8}>
          <TextInput
            className={action == "view" ? "df-input-title-read" : ""}
            label={action != "view" ? t("label.lineName") : null}
            isRequired={action != "view"}
            isReadonly={action == "view"}
            placeholder={t("txt.enterHere")}
            value={input.name}
            name="name"
            onChange={handleFieldChange}
          />
          <AlertBox text={error.name} />
        </Col>
      </Row>
      <div className="dm-divider pad-24"></div>
      <div className="df-container">
        {input.stations?.map((station, i) => {
          return (
            <div key={i} className="df-row">
              {i != 0 && (
                <div className="df-mid-details-container">
                  <Row gutter={[16, 0]}>
                    <Col>
                      <TextInput
                        className="df-time-input"
                        isRequired={action != "view"}
                        isReadonly={action == "view"}
                        label="Duration (in seconds)"
                        placeholder={t("txt.enterHere")}
                        value={input.stations[i].interStationETA_s}
                        name="interStationETA_s"
                        onChange={(e) => handleRowInputChange(e, i)}
                      />
                      <AlertBox
                        text={
                          error[`stations[${i}].interStationETA_s`]
                            ? t("error.required", {
                                field: t("label.interStationETA_s"),
                              })
                            : null
                        }
                      />
                    </Col>
                    <Col>
                      <TextInput
                        className="df-distance-input"
                        isRequired={action != "view"}
                        isReadonly={action == "view"}
                        label="Distance (in metres)"
                        placeholder={t("txt.enterHere")}
                        value={input.stations[i].interStationDistance_m}
                        name="interStationDistance_m"
                        onChange={(e) => handleRowInputChange(e, i)}
                      />
                      <AlertBox
                        text={error?.[`stations[${i}].interStationDistance_m`]}
                      />
                    </Col>
                  </Row>
                </div>
              )}
              <div className="df-content">
                <div className="df-content-header">
                  <div className="df-ch-station">
                    <div className="df-ch-station-header">
                      <div className="df-ch-title">Station {i + 1}</div>
                      <div className="df-ch-close">
                        {action != "view" && input.stations.length > 1 && (
                          <CloseOutlined onClick={() => deleteRows(i)} />
                        )}
                      </div>
                    </div>

                    <TextInput
                      isRequired={action != "view"}
                      isReadonly={action == "view"}
                      placeholder={t("txt.enterStationName")}
                      label={t("label.stationName")}
                      value={input.stations[i].stationName}
                      name="stationName"
                      onChange={(e) => handleRowInputChange(e, i)}
                    />
                  </div>
                  <AlertBox
                    text={
                      error[`stations[${i}].stationName`]
                        ? t("error.required", { field: t("label.stationName") })
                        : null
                    }
                  />
                </div>

                <div className="df-content-content">
                  <div className="df-cc-column">
                    <div>
                      <TextInput
                        isRequired={action != "view"}
                        isReadonly={action == "view"}
                        label={`${t("label.latitude")}`}
                        placeholder={t("txt.enterHere")}
                        value={input.stations[i].coordinate.lat}
                        name="lat"
                        onChange={(e) => handleCoordinatesChange(e, i)}
                      />
                    </div>
                    <AlertBox
                      text={
                        error[`stations[${i}].coordinate`]
                          ? t("error.required", {
                              field: t("label.coordinates"),
                            })
                          : null
                      }
                    />
                  </div>
                  <div className="df-cc-column">
                    <TextInput
                      isRequired={action != "view"}
                      isReadonly={action == "view"}
                      label={`${t("label.longitude")}`}
                      placeholder={t("txt.enterHere")}
                      value={input.stations[i].coordinate.lon}
                      name="lon"
                      onChange={(e) => handleCoordinatesChange(e, i)}
                    />
                  </div>
                  <div className="df-cc-column">
                    <RadioGroup
                      isRequired={action != "view"}
                      isReadonly={action == "view"}
                      label={t("label.interchangeableQuestion")}
                      onChange={(e) => {
                        onRadioChange(e, i);
                      }}
                      className="interchangeable-radio-button"
                      options={[
                        { value: true, label: "Yes" },
                        { value: false, label: "No" },
                      ]}
                      value={input.stations[i].interchangeable}
                    />
                  </div>
                  <AlertBox
                    text={
                      error[`stations[${i}].coordinate`]
                        ? t("error.required", { field: t("label.coordinates") })
                        : null
                    }
                  />
                </div>

                <div className="df-content-content">
                  <div className="df-cc-column">
                    {input.stations[i].interchangeable == true && (
                      <SelectInput
                        showSearch
                        isRequired={action != "view"}
                        isReadonly={action == "view"}
                        label={t("label.selectByTrainLine")}
                        mode="multiple"
                        onChange={(value: string | string[]) =>
                          handleSelectChange(value, "trainLineIDs", i)
                        }
                        options={trainLineOptions.map((line) => {
                          return {
                            label: line.name || "",
                            value: line._id || "",
                          };
                        })}
                        filterOption={(inputValue, option) =>
                          option.label
                            .toLowerCase()
                            .indexOf(inputValue.toLowerCase()) >= 0
                        }
                        value={input.stations[i].trainLineIDs}
                      />
                    )}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
      {action != "view" && (
        <div className="df-footer">
          <div className="df-row">
            <div className="df-add-row">
              <div className="df-ar-top"></div>
              <Button
                size="large"
                type="primary"
                onClick={() => addRows()}
                label={t("label.addField", { field: t("label.station") })}
                icon={<PlusCircleOutlined />}
              />
            </div>
          </div>
          <div>
            <Button
              size="large"
              type="primary"
              onClick={handleSubmit}
              label={id ? t("action.update") : t("action.create")}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default LineForm;
